import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";

import {
  createApplication,
  deleteApplication,
  getApplication,
  listAllMyApplications,
  listMyApplications,
  updateApplication,
  type ApplicationDetail,
  type CreateApplicationInput,
  type ListMyApplicationsQuery,
  type PaginatedApplications,
  type UpdateApplicationInput,
} from "../api/applications";
import { clearAllProductSelections } from "../utils/configure-storage";
import { useWorkFor } from "./useWorkFor";

const APPLICATIONS_KEY = ["applications"] as const;
// Selector eval keys on application ids, but its result reads their content (MICS-55 force).
const SELECTOR_EVAL_KEY = ["selector", "eval"] as const;

export function useMyApplications(
  query: ListMyApplicationsQuery = {},
  options?: Pick<UseQueryOptions<PaginatedApplications>, "enabled">,
) {
  return useQuery<PaginatedApplications>({
    queryKey: [...APPLICATIONS_KEY, "mine", query.limit ?? 50, query.offset ?? 0],
    queryFn: () => listMyApplications(query),
    staleTime: 30 * 1000,
    enabled: options?.enabled,
  });
}

// Fetch-all variant (issue #8): every saved application (no page cap).
// The active Work-For identity is part of the cache key: `/v1/applications/mine`
// is scoped server-side by the x-penn-work-for-* headers (attached by the Axios
// interceptor), so switching the Working-for target must refetch rather than
// serve the previous identity's cached list.
export function useAllMyApplications(
  options?: Pick<UseQueryOptions<PaginatedApplications>, "enabled">,
) {
  const workFor = useWorkFor();
  const identity =
    workFor.kind === "self" ? "self" : `${workFor.kind}:${workFor.email}`;
  return useQuery<PaginatedApplications>({
    queryKey: [...APPLICATIONS_KEY, "mine", "all", identity],
    queryFn: () => listAllMyApplications(),
    staleTime: 30 * 1000,
    enabled: options?.enabled,
  });
}

export function useApplication(
  id: string | null | undefined,
  options?: Pick<UseQueryOptions<ApplicationDetail>, "enabled">,
) {
  return useQuery<ApplicationDetail>({
    queryKey: [...APPLICATIONS_KEY, "detail", id],
    queryFn: () => getApplication(id as string),
    enabled: !!id && options?.enabled !== false,
    staleTime: 30 * 1000,
  });
}

export function useCreateApplication() {
  const qc = useQueryClient();
  return useMutation<ApplicationDetail, unknown, CreateApplicationInput>({
    mutationFn: createApplication,
    onSuccess: () => {
      // A new application changes which machine options the API recommends, so
      // any selection saved against the old scope is stale.
      clearAllProductSelections();
      qc.invalidateQueries({ queryKey: APPLICATIONS_KEY });
    },
  });
}

export function useUpdateApplication() {
  const qc = useQueryClient();
  return useMutation<
    ApplicationDetail,
    unknown,
    { id: string; body: UpdateApplicationInput }
  >({
    mutationFn: ({ id, body }) => updateApplication(id, body),
    onSuccess: (data) => {
      clearAllProductSelections();
      qc.invalidateQueries({ queryKey: APPLICATIONS_KEY });
      qc.invalidateQueries({ queryKey: SELECTOR_EVAL_KEY });
      qc.setQueryData([...APPLICATIONS_KEY, "detail", data.applicationId], data);
    },
  });
}

export function useDeleteApplication() {
  const qc = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: deleteApplication,
    onSuccess: () => {
      clearAllProductSelections();
      qc.invalidateQueries({ queryKey: APPLICATIONS_KEY });
      qc.invalidateQueries({ queryKey: SELECTOR_EVAL_KEY });
    },
  });
}
